// Э-6. Рабочее место оператора РБУ: полноэкранный режим, крупный шрифт, кнопки под сенсорный монитор.
// Госномер и марку оператор читает отсюда, а не узнаёт по телефону. Изменение очереди — звук и подсветка.

import { useEffect, useRef, useState } from "react";
import { fmtDate, fmtNum, fmtTime, localISODate } from "../../shared/format";
import { useLive } from "../../shared/live";
import { setQuery, useRoute } from "../../shared/router";
import { Button, Field, Input, Loading, Modal, NetBar, Plate, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, live, queue, useCtx } from "../ctx";
import { BalancesTable, InventoryDialog, ReceiptDialog, WriteoffDialog, type Balance } from "../components/StockParts";
import { MovesTable } from "./Stock";

export function OperatorPage({ online, queued, onLogout }: { online: boolean; queued: number; onLogout: () => void }) {
  const { me, refs } = useCtx();
  const route = useRoute();
  const myUnits = refs.units.filter((u) => me.all_plants || me.plant_ids.includes(u.plant_id));
  const unitId = route.query.get("unit") || readUnit() || myUnits[0]?.id;
  const [theme, setTheme] = useState<string>(() => readTheme());
  const [tab, setTab] = useState<"queue" | "task" | "output" | "stock" | "shift">("queue");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("rbs.beton.operatorTheme", theme); } catch { /* без сохранения */ }
    return () => { document.documentElement.dataset.theme = "light"; };
  }, [theme]);
  useEffect(() => { if (unitId) try { localStorage.setItem("rbs.beton.unit", unitId); } catch { /* */ } }, [unitId]);

  const unit = refs.units.find((u) => u.id === unitId);
  return (
    <div className="operator">
      <NetBar online={online} queued={queued} />
      <div className="head">
        <h1>{unit ? `${unit.plant_name}, ${unit.name}` : "РБУ не выбрана"}</h1>
        {myUnits.length > 1 && (
          <Select aria-label="РБУ" value={unitId || ""} onChange={(e) => setQuery({ unit: e.target.value })} style={{ width: 220 }}
            options={myUnits.map((u) => ({ value: u.id, label: `${u.plant_name}, ${u.name}` }))} />
        )}
        <div className="grow" />
        <span className="row tight small"><span className={`dot${online ? "" : " off"}`} />{online ? "Онлайн" : "Нет связи"}</span>
        <span className="small muted">оператор {me.full_name}</span>
        <Clock />
        <Button size="small" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? "Светлая тема" : "Ночная тема"}</Button>
        {me.role !== "operator" && <Button size="small" variant="ghost" onClick={() => (location.hash = "/schedule")}>К графику</Button>}
        <Button size="small" variant="ghost" onClick={onLogout}>Выйти</Button>
      </div>
      {!unitId ? <div className="content"><p>Вам не назначен завод. Обратитесь к администратору.</p></div> : (
        <>
          <div style={{ padding: "0 22px" }}>
            <Tabs value={tab} onChange={setTab} items={[{ value: "queue", label: "Очередь" }, { value: "task", label: "Сменное задание" }, { value: "output", label: "Выпуск за смену" }, { value: "stock", label: "Сырьё" }, { value: "shift", label: "Смена" }]} />
          </div>
          {tab === "queue" && <QueueView unitId={unitId} />}
          {tab === "task" && <ShiftTask unitId={unitId} />}
          {tab === "output" && <ShiftOutput unitId={unitId} />}
          {tab === "stock" && unit && <PlantStock plantId={unit.plant_id} plantName={unit.plant_name} />}
          {tab === "shift" && <OperatorShift unitId={unitId} />}
        </>
      )}
    </div>
  );
}

function readUnit(): string | null {
  try { return localStorage.getItem("rbs.beton.unit"); } catch { return null; }
}
function readTheme(): string {
  try { return localStorage.getItem("rbs.beton.operatorTheme") || "light"; } catch { return "light"; }
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 15_000); return () => clearInterval(t); }, []);
  return <b className="num">{fmtTime(now)}</b>;
}

function beep() {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    g.gain.value = 0.15;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.25);
  } catch { /* звук недоступен */ }
}

function QueueView({ unitId }: { unitId: string }) {
  const toast = useToast();
  const q = useAsync<any>(() => api.get("/operator/queue", { unit: unitId }), [unitId]);
  const [flash, setFlash] = useState(false);
  const [finish, setFinish] = useState<any | null>(null);
  const [ret, setRet] = useState<any | null>(null);
  const [manual, setManual] = useState(false);
  const sig = useRef("");
  useLive(live, ["trip.", "order."], () => q.reload(), 150);

  useEffect(() => {
    const items = q.data ? [q.data.current, ...q.data.next].filter(Boolean) : [];
    const s = items.map((i: any) => `${i.id}:${i.version}`).join("|");
    if (sig.current && s !== sig.current) {
      beep();
      setFlash(true);
      setTimeout(() => setFlash(false), 2600);
    }
    sig.current = s;
  }, [q.data]);

  if (!q.data) return <Loading error={q.error} onRetry={q.reload} />;
  const cur = q.data.current;

  const start = async () => {
    try {
      const r = await queue.send("POST", `/operator/trips/${cur.id}/load-start`, { version: cur.version }, `Замес начат: ${cur.plate}`);
      if (r === null) toast.ok("Нет связи: отметка сохранится и уйдёт автоматически");
      q.reload();
    } catch (e) { toast.error(e); q.reload(); }
  };

  return (
    <div className="body">
      <div className="stack">
        {!cur ? (
          <div className="now-card"><h2>Очередь пуста</h2><p className="muted">Новые машины появятся здесь автоматически, как только диспетчер назначит их на рейс.</p></div>
        ) : (
          <div className={`now-card${flash ? " flash" : ""}`}>
            <div className="muted strong">{cur.status === "loading" ? "Сейчас грузим" : "Следующая машина"}</div>
            <div><Plate value={cur.plate} />{cur.driver_name && <span style={{ fontSize: 19, marginLeft: 12 }}>{cur.driver_name}</span>}</div>
            <div style={{ fontSize: 30, fontWeight: 700 }}>{cur.grade}, <span className="num">{fmtNum(cur.volume_m3)} м³</span></div>
            <div style={{ fontSize: 19 }}>{cur.site_name}, {cur.customer_name}, рейс {cur.seq} из {cur.trips_total}</div>
            {cur.comment_operator && <div className="alert" style={{ fontSize: 18 }}><b>Комментарий:</b> {cur.comment_operator}</div>}
            <div className="cols-2">
              {cur.status === "loading" ? (
                <Button size="big" disabled>✓ Замес начат в {fmtTime(cur.load_started_at)}</Button>
              ) : (
                <Button size="big" onClick={start}>Замес начат</Button>
              )}
              <Button size="big" variant="primary" disabled={cur.status !== "loading"} onClick={() => setFinish(cur)}>Погрузка завершена</Button>
            </div>
          </div>
        )}
        <div className="row">
          <Button onClick={() => setManual(true)}>Отгрузка без системы (нет связи)</Button>
          <Button variant="ghost" onClick={() => setRet({})}>Возврат бетона</Button>
        </div>
      </div>
      <div className="stack">
        <h2>Далее в очереди</h2>
        {q.data.next.length === 0 && <p className="muted">Больше машин в очереди нет.</p>}
        {q.data.next.slice(0, 6).map((n: any) => (
          <div key={n.id} className={`queue-item${n.changed ? " changed" : ""}`}>
            <div className="time">{n.slot_local}</div>
            <div>
              <Plate value={n.plate} small />{n.driver_name && <span className="small" style={{ marginLeft: 8 }}>{n.driver_name}</span>}
              <div className="small muted" style={{ marginTop: 4 }}>{n.grade}, {fmtNum(n.volume_m3)} м³, {n.site_name}</div>
            </div>
            {n.changed && <span className="small strong">изменено</span>}
          </div>
        ))}
        <p className="small muted">Изменение очереди — звук и подсветка. Цены оператору не показываются.</p>
      </div>
      {finish && <FinishDialog trip={finish} onClose={() => setFinish(null)} onDone={() => { setFinish(null); q.reload(); }} />}
      {ret && <ReturnDialog unitId={unitId} onClose={() => setRet(null)} />}
      {manual && <ManualDialog unitId={unitId} onClose={() => setManual(false)} />}
    </div>
  );
}

function FinishDialog({ trip, onClose, onDone }: { trip: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [vol, setVol] = useState(String(trip.volume_m3));
  const [reason, setReason] = useState("");
  const dev = Math.abs(Number(vol) - trip.volume_m3) > 0.1;
  return (
    <Modal title="Погрузка завершена" onClose={onClose} actions={<>
      <Button onClick={onClose}>Назад</Button>
      <Button variant="primary" size="big" disabled={!Number(vol) || (dev && reason.trim().length < 2)} onClick={async () => {
        try {
          const r: any = await queue.send("POST", `/operator/trips/${trip.id}/load-finish`, { version: trip.version, actual_volume_m3: Number(vol), deviation_reason: dev ? reason : null }, `Погрузка завершена: ${trip.plate}`);
          toast.ok(r ? `Машина выехала, накладная №${r.waybill_number}` : "Нет связи: отметка уйдёт автоматически");
          onDone();
        } catch (e) { toast.error(e); }
      }}>Подтвердить</Button>
    </>}>
      <Field label="Фактический объём, м³">
        <Input inputMode="decimal" value={vol} onChange={(e) => setVol(e.target.value.replace(",", "."))} style={{ fontSize: 28, minHeight: 60 }} />
      </Field>
      {dev && (
        <Field label="Причина отклонения" hint={`План ${fmtNum(trip.volume_m3)} м³`}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
}

function ReturnDialog({ unitId, onClose }: { unitId: string; onClose: () => void }) {
  const toast = useToast();
  const out = useAsync<any>(() => api.get("/operator/shift-output", { unit: unitId }), [unitId]);
  const [batch, setBatch] = useState("");
  const [kind, setKind] = useState("returned");
  const [vol, setVol] = useState("");
  const [reason, setReason] = useState("");
  const [extra, setExtra] = useState(false);
  const batches = (out.data?.batches || []).filter((b: any) => b.trip_id);
  return (
    <Modal title="Возврат или утилизация бетона" onClose={onClose} actions={<>
      <Button onClick={onClose}>Закрыть</Button>
      <Button variant="primary" disabled={!batch || !Number(vol) || reason.trim().length < 2} onClick={async () => {
        const b = batches.find((x: any) => x.id === batch);
        try {
          await api.post(`/operator/trips/${b.trip_id}/return`, { kind, volume_m3: Number(vol), reason, add_trip: extra });
          toast.ok(extra ? "Отмечено, дополнительный рейс добавлен в заявку" : "Отмечено");
          onClose();
        } catch (e) { toast.error(e); }
      }}>Сохранить</Button>
    </>}>
      <Field label="Замес">
        <Select value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="Выберите погрузку"
          options={batches.map((b: any) => ({ value: b.id, label: `${fmtTime(b.finished_at)}, ${b.grade}, ${b.fact_m3} м³` }))} />
      </Field>
      <div className="form-grid">
        <Field label="Что сделали"><Select value={kind} onChange={(e) => setKind(e.target.value)} options={[{ value: "returned", label: "Возврат на завод" }, { value: "utilized", label: "Утилизация" }]} /></Field>
        <Field label="Объём, м³"><Input inputMode="decimal" value={vol} onChange={(e) => setVol(e.target.value.replace(",", "."))} /></Field>
      </div>
      <Field label="Причина"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <label className="check"><input type="checkbox" checked={extra} onChange={(e) => setExtra(e.target.checked)} />Добавить дополнительный рейс в ту же заявку</label>
    </Modal>
  );
}

function ManualDialog({ unitId, onClose }: { unitId: string; onClose: () => void }) {
  const { refs } = useCtx();
  const toast = useToast();
  const [grade, setGrade] = useState("");
  const [vol, setVol] = useState("9");
  const [plate, setPlate] = useState("");
  return (
    <Modal title="Отгрузка без системы" onClose={onClose} actions={<>
      <Button onClick={onClose}>Закрыть</Button>
      <Button variant="primary" disabled={!grade || !Number(vol) || plate.length < 6} onClick={async () => {
        try {
          const r = await queue.send("POST", "/operator/manual-shipments", { unit_id: unitId, grade_id: grade, volume_m3: Number(vol), plate, at: new Date().toISOString() }, `Ручная отгрузка ${plate}`);
          toast.ok(r ? "Сохранено. Диспетчер привяжет отгрузку к заявке" : "Нет связи: сохранится автоматически");
          onClose();
        } catch (e) { toast.error(e); }
      }}>Сохранить</Button>
    </>}>
      <p className="small muted">Если связи с сервером нет, отметка сохранится на этом компьютере и уйдёт, когда связь появится. Потом её привязывают к заявке.</p>
      <Field label="Госномер"><Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="А123ВС50" /></Field>
      <div className="form-grid">
        <Field label="Марка"><Select value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="—" options={refs.grades.map((g) => ({ value: g.id, label: g.code }))} /></Field>
        <Field label="Объём, м³"><Input inputMode="decimal" value={vol} onChange={(e) => setVol(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function ShiftTask({ unitId }: { unitId: string }) {
  const toast = useToast();
  const t = useAsync<any>(() => api.get("/operator/shift-task", { unit: unitId }), [unitId]);
  useLive(live, ["trip.", "order."], () => t.reload());
  if (!t.data) return <div className="content"><Loading error={t.error} /></div>;
  return (
    <div className="content">
      <div className="row between">
        <div>
          <h2>Сменное задание: {fmtNum(t.data.total_m3)} м³</h2>
          <p className="muted small">Смена {fmtDate(t.data.from)} {fmtTime(t.data.from)} — {fmtDate(t.data.to)} {fmtTime(t.data.to)}. Обновляется сразу при изменении графика.</p>
        </div>
        <Button onClick={() => api.open("/operator/shift-task/print", { unit: unitId }).catch((e) => toast.error(e))}>Печать</Button>
      </div>
      <div className="row">{t.data.totals.map((x: any) => <span key={x.grade} className="tag plain">{x.grade}: <b className="num">{fmtNum(x.volume_m3)} м³</b></span>)}</div>
      <div className="panel table-wrap">
        <table className="table">
          <thead><tr><th>Время</th><th>Марка</th><th className="num">м³</th><th>Объект</th><th>Заявка</th><th>Машина</th></tr></thead>
          <tbody>{t.data.rows.map((r: any) => (
            <tr key={r.trip_id}><td className="tabular">{r.time}</td><td>{r.grade}</td><td className="num">{fmtNum(r.volume_m3)}</td><td>{r.site_name}</td><td className="tabular">№{r.order_number}/{r.seq}</td><td>{r.plate || "—"}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function ShiftOutput({ unitId }: { unitId: string }) {
  const toast = useToast();
  const o = useAsync<any>(() => api.get("/operator/shift-output", { unit: unitId }), [unitId]);
  useLive(live, ["batch.", "trip."], () => o.reload());
  if (!o.data) return <div className="content"><Loading error={o.error} /></div>;
  const d = o.data;
  return (
    <div className="content">
      <div className="kpis">
        <div className="kpi"><div className="k">Выпущено</div><div className="v">{fmtNum(d.fact_m3)} м³</div><div className="s">по заданию {fmtNum(d.plan_m3)} м³</div></div>
        <div className="kpi"><div className="k">Замесов</div><div className="v">{d.batches.length}</div></div>
        <div className="kpi"><div className="k">Возврат и утилизация</div><div className="v">{fmtNum(d.returned_m3)} м³</div></div>
      </div>
      <div className="panel table-wrap">
        <table className="table">
          <thead><tr><th>Марка</th><th className="num">Задание, м³</th><th className="num">Факт, м³</th></tr></thead>
          <tbody>{d.by_grade.map((g: any) => <tr key={g.grade}><td>{g.grade}</td><td className="num">{fmtNum(g.plan_m3)}</td><td className="num">{fmtNum(g.fact_m3)}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="panel table-wrap">
        <div className="ph"><h2 className="grow">Погрузки по машинам</h2>
          <Button onClick={() => api.open("/operator/shift-output/print", { unit: unitId, start: d.from, end: d.to }).catch((e) => toast.error(e))}>Печать</Button></div>
        {d.batches.length === 0 ? <p className="muted pb" style={{ margin: 0 }}>Погрузок за смену пока нет.</p> : (
          <table className="table">
            <thead><tr><th>Начало погрузки</th><th>Конец</th><th className="num">Мин</th><th>Машина</th><th>Водитель</th><th>Заявка</th><th>Марка</th><th className="num">м³</th></tr></thead>
            <tbody>{d.batches.map((b: any) => (
              <tr key={b.id}><td className="tabular">{fmtTime(b.started_at)}</td><td className="tabular">{b.finished_at ? fmtTime(b.finished_at) : "—"}</td>
                <td className="num">{b.finished_at ? Math.round((Date.parse(b.finished_at) - Date.parse(b.started_at)) / 60000) : "—"}</td>
                <td>{b.plate ? <b className="num">{b.plate}</b> : "—"}</td><td>{b.driver || ""}</td>
                <td className="tabular">{b.order_number ? `№${b.order_number}/${b.seq}` : "без системы"}</td><td>{b.grade}</td><td className="num">{fmtNum(b.fact_m3)}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** П-3 (22.10.2026): смена оператора РБУ — открыть, сдать с заметками, принять у предыдущего, как у диспетчеров. */
function OperatorShift({ unitId }: { unitId: string }) {
  const toast = useToast();
  const s = useAsync<any>(() => api.get("/shifts/current/summary", { kind: "operator", unit: unitId, hours: 24 }), [unitId]);
  useLive(live, ["shift."], () => s.reload(), 500);
  const [closing, setClosing] = useState(false);
  const [notes, setNotes] = useState("");
  const run = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast.ok(ok); s.reload(); } catch (e) { toast.error(e); }
  };
  if (!s.data) return <div className="content"><Loading error={s.error} onRetry={s.reload} /></div>;
  const d = s.data;
  const prev = d.previous_shift;
  const cur = d.current_shift;
  return (
    <div className="content stack" style={{ maxWidth: 760 }}>
      {cur ? (
        <div className="panel pad stack tight">
          <h2 style={{ margin: 0 }}>Смену ведёт {cur.user}</h2>
          <div className="muted">с {fmtDate(cur.opened_at)} {fmtTime(cur.opened_at)}</div>
          {cur.mine && <div><Button size="big" variant="primary" onClick={() => setClosing(true)}>Сдать смену</Button></div>}
        </div>
      ) : (
        <div className="panel pad stack tight">
          <h2 style={{ margin: 0 }}>Смена не открыта</h2>
          {prev && !prev.accepted_at ? (
            <>
              <div className="muted">Смену сдал(а) {prev.user} в {fmtTime(prev.closed_at)}. Прочитайте заметки и примите смену.</div>
              <div><Button size="big" variant="primary" onClick={() => run(() => api.post(`/shifts/${prev.id}/accept`), "Смена принята")}>Принять смену</Button></div>
            </>
          ) : (
            <div><Button size="big" variant="primary" onClick={() => run(() => api.post("/shifts/open", { kind: "operator", unit_id: unitId }), "Смена открыта")}>Открыть смену</Button></div>
          )}
        </div>
      )}
      {prev && (
        <div className="alert info">
          <div className="t">Заметки предыдущей смены: {prev.user}, сдана {fmtDate(prev.closed_at)} {fmtTime(prev.closed_at)}</div>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{prev.notes || "Заметок нет."}</div>
          {prev.accepted_by && <div className="d">Принял(а): {prev.accepted_by}</div>}
        </div>
      )}
      {closing && cur && (
        <Modal title="Сдать смену" onClose={() => setClosing(false)} actions={<>
          <Button onClick={() => setClosing(false)}>Отмена</Button>
          <Button variant="primary" onClick={() => run(async () => { await api.post(`/shifts/${cur.id}/close`, { handover_notes: notes }); setClosing(false); setNotes(""); }, "Смена сдана")}>Сдать смену</Button>
        </>}>
          <Field label="Заметки для следующего оператора" hint="Что важно знать сменщику: неисправности, остатки, договорённости">
            <textarea className="input" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Например: дозатор цемента барахлит" />
          </Field>
        </Modal>
      )}
    </div>
  );
}

/** Сырьё своего завода: остатки, приход, списание, инвентаризация (Этап 3). */
function PlantStock({ plantId, plantName }: { plantId: string; plantName: string }) {
  const bal = useAsync<{ rows: Balance[] }>(() => api.get("/stock/balances", { plant: plantId }), [plantId]);
  const today = localISODate();
  const mov = useAsync<any[]>(() => api.get("/stock/moves", { plant: plantId, start: today, end: today }), [plantId]);
  const [dlg, setDlg] = useState<"" | "receipt" | "writeoff" | "inventory">("");
  const reload = () => { bal.reload(); mov.reload(); };
  useLive(live, ["stock.", "batch."], reload, 800);
  const plants = [{ id: plantId, name: plantName }];
  const done = () => { setDlg(""); reload(); };
  return (
    <div className="content stack">
      <div className="row">
        <Button size="big" variant="primary" onClick={() => setDlg("receipt")}>Приход сырья</Button>
        <Button size="big" onClick={() => setDlg("writeoff")}>Списание</Button>
        <Button size="big" onClick={() => setDlg("inventory")}>Инвентаризация</Button>
      </div>
      {!bal.data ? <Loading error={bal.error} onRetry={bal.reload} /> : <BalancesTable rows={bal.data.rows} />}
      <h3>Движения сегодня</h3>
      {!mov.data ? <Loading error={mov.error} /> : <MovesTable rows={mov.data.slice(0, 100)} />}
      {dlg === "receipt" && <ReceiptDialog plants={plants} plantId={plantId} onClose={() => setDlg("")} onDone={done} />}
      {dlg === "writeoff" && <WriteoffDialog plants={plants} plantId={plantId} onClose={() => setDlg("")} onDone={done} />}
      {dlg === "inventory" && <InventoryDialog plants={plants} plantId={plantId} onClose={() => setDlg("")} onDone={done} />}
    </div>
  );
}
